import React, { useRef, useState, useEffect } from 'react';
import { Accordion, Form, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { ExclamationCircleFill, ThreeDots } from 'react-bootstrap-icons';
import { formatMessage } from '../../../translations/format-message';
import img404 from '../../../assets/Images/404.jpg';
import { FormattedMessage } from 'react-intl';
import TeplarPrintTable from '../components/teplar-table';
import BOMTable from '../components/bom-table';
import CheckItemsData from '../components/check-item-data';
import LEDCut from '../components/led-cut-table';
import LEDLength from '../components/led-length-table';
import CanvasDrawOnImage from '../../../shared-components/canvas-draw-on-image/canvas-draw-on-image';
import PdfViewer from '../../../shared-components/pdf-viewer/pdf-viewer';
import ToolBomRelationTable from '../components/tool-bom-relation-table';
import TargetToolList from '../components/target-tool-list';

const MIN_COL_PERCENT = 20;
const MAX_COL_PERCENT = 80;
const LONG_PRESS_MS = 400;
const LONG_PRESS_MOVE_TOLERANCE = 8;
const SPLIT_HANDLE_PX = 16;

interface OperatorHorizontalViewProps {
  operatorTeplarDetails: any[];
  mergedTabs: any[];
  activeIndex: number;
  setActiveIndex: (index: number) => void;
  setMemo: React.Dispatch<React.SetStateAction<string[]>>;
  setSubmittedCheckItems: React.Dispatch<React.SetStateAction<any>>;
  dimensionTable: any[];
  dims: any[];
  dimensionChecks: { [key: string]: boolean };
  handleDimensionCheck: (
    tabIndex: number,
    rowIndex: number,
    dimId: number,
    checked: boolean
  ) => void;
  isTimerRunning: boolean;
  productImages: any[];
  processType: number | string;
  isProcessStarted: boolean;
  handleDecrementPrintCount: (index: number) => void;
  printCount: number[];
  handleChangePrintCount: (
    e: React.ChangeEvent<HTMLInputElement>,
    index: number
  ) => void;
  handleIncrementPrintCount: (index: number) => void;
  stringToHex: (text: string) => string;
  onSaveTeplarOrder: (
    orderData: { Id: number; Order: number; IsPrinted: boolean }[]
  ) => void;
  onTeplarOrderChange?: (
    orderData: { Id: number; Order: number; IsPrinted: boolean }[]
  ) => void;
  checkItems: any[];
  submittedCheckItems: any;
  handleCheckboxChange: (
    tabIndex: number,
    itemId: number,
    checked: boolean
  ) => void;
  handleMemoChange: (tabIndex: number, value: string) => void;
  memo: Record<number, string>;
  handleInfoChange: (tabIndex: number, itemId: number, value: string) => void;
  validFiles: any[];
  allFilesAreNull: boolean;
  canvasDrawings: Record<string, string>;
  handleCanvasChange: (key: string, dataUrl: string) => void;
  bomDataListState: any[];
  setBomDataListState: React.Dispatch<React.SetStateAction<any[]>>;
  tabValidationMap: Record<number, boolean>;
  showValidationWarnings: boolean;
  bomDataList: any[];
  teplarList: any[];
  setTeplarList: React.Dispatch<React.SetStateAction<any[]>>;
  saveClicked: boolean;
  setSaveClicked: React.Dispatch<React.SetStateAction<boolean>>;
  ledCutList: any[];
  ledCutListState: any[];
  setLedCutListState: React.Dispatch<React.SetStateAction<any[]>>;
  openLEDHandler: (event: any) => void;
  ledLengthList: any[];
  ledLengthListState: any[];
  setLedLengthListState: React.Dispatch<React.SetStateAction<any[]>>;
  onToolInspectionOpen?: () => void;
}

const OperatorHorizontalView: React.FC<OperatorHorizontalViewProps> = ({
  operatorTeplarDetails,
  mergedTabs,
  activeIndex,
  setActiveIndex,
  setMemo,
  setSubmittedCheckItems,
  dimensionTable,
  dims,
  dimensionChecks,
  handleDimensionCheck,
  isTimerRunning,
  productImages,
  processType,
  isProcessStarted,
  handleDecrementPrintCount,
  printCount,
  handleChangePrintCount,
  handleIncrementPrintCount,
  stringToHex,
  onSaveTeplarOrder,
  onTeplarOrderChange,
  checkItems,
  submittedCheckItems,
  handleCheckboxChange,
  handleMemoChange,
  memo,
  handleInfoChange,
  validFiles,
  allFilesAreNull,
  canvasDrawings,
  handleCanvasChange,
  bomDataListState,
  setBomDataListState,
  tabValidationMap,
  showValidationWarnings,
  bomDataList,
  teplarList,
  setTeplarList,
  saveClicked,
  setSaveClicked,
  ledCutList,
  ledCutListState,
  setLedCutListState,
  openLEDHandler,
  ledLengthList,
  ledLengthListState,
  setLedLengthListState,
  onToolInspectionOpen,
}) => {
  const openFilesInNewWindow = (fileDetails: any[] = []) => {
    const files = fileDetails.filter((file: any) => file?.S3FilePath);
    if (files.length === 0) return;

    const newWindow = window.open('', '_blank');
    if (!newWindow) return;

    const fileMarkup = files
      .map((file: any) => {
        if (file.FileType === 'application/pdf') {
          return `<iframe src="${file.S3FilePath}#view=FitH&zoom=page-width" style="width:100%;min-height:100vh;border:none;margin-bottom:0px;"></iframe>`;
        }
        return `<img src="${file.S3FilePath}" style="width:100%;margin-bottom:12px;" />`;
      })
      .join('');

    newWindow.document.write(`
      <html>
        <body style="margin:0">
          ${fileMarkup}
        </body>
      </html>
    `);
    newWindow.document.close();
  };

  // Resizable split (left/right columns). Stored in localStorage, which is
  // cleared on logout.
  const splitStorageKey = `operator_horizontal_left_width_${processType}`;
  const defaultLeftWidth = Number(processType) === 3 ? 33.33 : 66.67;
  const readSavedWidth = (): number => {
    try {
      const saved = parseFloat(localStorage.getItem(splitStorageKey) || '');
      if (!isNaN(saved) && saved >= MIN_COL_PERCENT && saved <= MAX_COL_PERCENT)
        return saved;
    } catch {
      // ignore storage errors
    }
    return defaultLeftWidth;
  };
  const rowRef = useRef<HTMLDivElement>(null);
  const longPressTimer = useRef<number | null>(null);
  const pressStart = useRef<{ x: number; y: number } | null>(null);
  const isResizingRef = useRef(false);
  const [isResizing, setIsResizing] = useState(false);
  const [isPressing, setIsPressing] = useState(false);
  const [leftWidth, setLeftWidth] = useState<number>(readSavedWidth);
  const leftWidthRef = useRef(leftWidth);
  leftWidthRef.current = leftWidth;

  useEffect(() => {
    setLeftWidth(readSavedWidth());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [splitStorageKey]);

  const clearLongPress = () => {
    if (longPressTimer.current !== null) {
      window.clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }
    pressStart.current = null;
    setIsPressing(false);
  };

  const updateWidthFromPointer = (clientX: number) => {
    const rect = rowRef.current?.getBoundingClientRect();
    if (!rect || rect.width === 0) return;
    const pct = ((clientX - rect.left) / rect.width) * 100;
    setLeftWidth(
      Math.min(
        MAX_COL_PERCENT,
        Math.max(MIN_COL_PERCENT, Number(pct.toFixed(2)))
      )
    );
  };

  const handleSplitPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    if (e.pointerType === 'mouse' && e.button !== 0) return;
    const target = e.currentTarget;
    const pointerId = e.pointerId;
    pressStart.current = { x: e.clientX, y: e.clientY };

    const activate = () => {
      isResizingRef.current = true;
      setIsResizing(true);
      try {
        target.setPointerCapture(pointerId);
      } catch {
        // pointer may already be released
      }
      if (navigator.vibrate) navigator.vibrate(30);
    };

    // Mouse drags immediately; touch/pen need a long press.
    if (e.pointerType === 'mouse') {
      activate();
    } else {
      setIsPressing(true);
      longPressTimer.current = window.setTimeout(() => {
        longPressTimer.current = null;
        setIsPressing(false);
        activate();
      }, LONG_PRESS_MS);
    }
  };

  const handleSplitPointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (isResizingRef.current) {
      updateWidthFromPointer(e.clientX);
      return;
    }
    // Moved too far before the long press completed: cancel it.
    if (pressStart.current) {
      const dx = Math.abs(e.clientX - pressStart.current.x);
      const dy = Math.abs(e.clientY - pressStart.current.y);
      if (dx > LONG_PRESS_MOVE_TOLERANCE || dy > LONG_PRESS_MOVE_TOLERANCE)
        clearLongPress();
    }
  };

  const handleSplitPointerEnd = (e: React.PointerEvent<HTMLDivElement>) => {
    clearLongPress();
    if (!isResizingRef.current) return;
    isResizingRef.current = false;
    setIsResizing(false);
    try {
      e.currentTarget.releasePointerCapture(e.pointerId);
    } catch {
      // already released
    }
    try {
      localStorage.setItem(splitStorageKey, String(leftWidthRef.current));
    } catch {
      // ignore storage errors
    }
  };

  useEffect(() => clearLongPress, []);

  const ulRef = useRef<HTMLUListElement>(null);
  const tabRefs = useRef<(HTMLLIElement | null)[]>([]);
  const toolBomTabRef = useRef<HTMLLIElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [isOverflowing, setIsOverflowing] = useState(false);
  const [showTabDropdown, setShowTabDropdown] = useState(false);
  const [activeAccordionKey, setActiveAccordionKey] = useState<string>('0');

  useEffect(() => {
    if (activeIndex !== mergedTabs.length && activeAccordionKey === '5') {
      setActiveAccordionKey('0');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeIndex]);

  useEffect(() => {
    const checkOverflow = () => {
      if (ulRef.current) {
        setIsOverflowing(ulRef.current.scrollWidth > ulRef.current.clientWidth);
      }
    };
    checkOverflow();
    const observer = new ResizeObserver(checkOverflow);
    if (ulRef.current) observer.observe(ulRef.current);
    return () => observer.disconnect();
  }, [mergedTabs]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target as Node)
      ) {
        setShowTabDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const scrollTabIntoViewIfNeeded = (originalIndex: number) => {
    const tabEl = tabRefs.current[originalIndex];
    const ulEl = ulRef.current;
    if (!tabEl || !ulEl) return;

    const tabRect = tabEl.getBoundingClientRect();
    const ulRect = ulEl.getBoundingClientRect();
    const isFullyVisible =
      tabRect.left >= ulRect.left && tabRect.right <= ulRect.right;

    if (!isFullyVisible) {
      tabEl.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'center',
      });
    }
  };

  const handleDropdownTabClick = (originalIndex: number) => {
    setActiveIndex(originalIndex);
    setShowTabDropdown(false);
    scrollTabIntoViewIfNeeded(originalIndex);
  };

  const sortedTabs = mergedTabs
    .map((item, index) => ({ item, originalIndex: index }))
    .sort((a, b) => {
      const isTrue = (val: any) =>
        val === true || String(val).toLowerCase() === 'true';
      const getPriority = (item: any) => {
        if (isTrue(item.data?.IsDefault) || isTrue(item.data?.isDefault))
          return 0;
        if (isTrue(item.data?.IsDimension) || isTrue(item.data?.isDimension))
          return 1;
        return 2;
      };
      return getPriority(a.item) - getPriority(b.item);
    });

  return (
    <div className="layout-container horizontal">
      <div
        ref={rowRef}
        className={`row g-3 flex-nowrap operator-split-row${isResizing ? ' is-resizing' : ''}`}
      >
        {/* Box 1: Tabs and Images/Dimension Table */}
        <div
          className={`${Number(processType) === 3 ? 'picking-horizontal' : Number(processType) === 2 ? '' : 'print-horizontal-dimension'}`}
          style={{
            flex: `0 0 calc((100% - ${SPLIT_HANDLE_PX}px) * ${leftWidth / 100})`,
            maxWidth: `calc((100% - ${SPLIT_HANDLE_PX}px) * ${leftWidth / 100})`,
          }}
        >
          <div className="operator-tabs-dimesnion position-relative">
            <ul
              ref={ulRef}
              className={`nav nav-tabs ${isOverflowing ? 'overflowing' : ''}`}
              id="imageDataTypeTab"
              role="tablist"
            >
              {sortedTabs.map(({ item: tabItem, originalIndex }) => {
                const hasTabWarning =
                  showValidationWarnings &&
                  tabValidationMap[originalIndex] === false;

                return (
                  <OverlayTrigger
                    key={originalIndex}
                    container={ulRef}
                    placement="top"
                    overlay={
                      <Tooltip id={`operator-tab-title-${originalIndex}`}>
                        {tabItem.Heading}
                      </Tooltip>
                    }
                  >
                    <li
                      ref={(el) => {
                        tabRefs.current[originalIndex] = el;
                      }}
                      className={`textTabBlock${activeIndex === originalIndex ? 'active textTabBlockactive' : ''}`}
                      onClick={() => {
                        setActiveIndex(originalIndex);
                        const currentFileItem = tabItem.data;
                        const memoKey =
                          currentFileItem?.TabIndex ?? originalIndex;

                        setMemo((prev) => {
                          if (prev[memoKey] !== undefined) return prev;
                          return { ...prev, [memoKey]: '' };
                        });

                        setSubmittedCheckItems((prev: any) => {
                          if (tabItem.data?.CheckItems) {
                            const hasAnyCheckItem =
                              tabItem.data.CheckItems.some(
                                (item: any) => prev[item.Id] !== undefined
                              );
                            if (hasAnyCheckItem) return prev;
                          }
                          return prev;
                        });

                        scrollTabIntoViewIfNeeded(originalIndex);
                      }}
                      onDoubleClick={() =>
                        openFilesInNewWindow(tabItem.data?.FileDetail || [])
                      }
                    >
                      <button className="nav-link d-flex align-items-center gap-1">
                        <span>{tabItem.Heading}</span>
                        {hasTabWarning && (
                          <OverlayTrigger
                            placement="top"
                            overlay={
                              // <Tooltip
                              //   id={`operator-tab-warning-${originalIndex}`}
                              // >
                              //   {formatMessage('please_add_check_item')}
                              // </Tooltip>
                              <></>
                            }
                          >
                            <ExclamationCircleFill className="text-danger" />
                          </OverlayTrigger>
                        )}
                      </button>
                    </li>
                  </OverlayTrigger>
                );
              })}
              <li
                ref={toolBomTabRef}
                className={`textTabBlock${activeIndex === mergedTabs.length ? 'active textTabBlockactive' : ''}`}
                onClick={() => setActiveIndex(mergedTabs.length)}
              >
                <button className="nav-link d-flex align-items-center gap-1">
                  <span>{formatMessage('tool_bom')}</span>
                </button>
              </li>
            </ul>
            {isOverflowing && (
              <div
                ref={dropdownRef}
                className="position-absolute end-0 top-0 d-flex align-items-center z-5"
              >
                <button
                  className="btn py-2 btn-secondary"
                  onClick={() => setShowTabDropdown((prev) => !prev)}
                  title="All tabs"
                >
                  <ThreeDots />
                </button>
                {showTabDropdown && (
                  <div
                    className="dropdown-menu show"
                    style={{
                      position: 'absolute',
                      right: 0,
                      top: '100%',
                      minWidth: '180px',
                      maxHeight: '260px',
                      overflowY: 'auto',
                    }}
                  >
                    {sortedTabs.map(({ item: tabItem, originalIndex }) => (
                      <button
                        key={originalIndex}
                        className={`dropdown-item${activeIndex === originalIndex ? ' active' : ''}`}
                        onClick={() => handleDropdownTabClick(originalIndex)}
                      >
                        {tabItem.Heading}
                      </button>
                    ))}
                    <button
                      className={`dropdown-item${activeIndex === mergedTabs.length ? ' active' : ''}`}
                      onClick={() => {
                        setActiveIndex(mergedTabs.length);
                        setShowTabDropdown(false);
                        toolBomTabRef.current?.scrollIntoView({
                          behavior: 'smooth',
                          block: 'nearest',
                          inline: 'center',
                        });
                      }}
                    >
                      {formatMessage('tool_bom')}
                    </button>
                  </div>
                )}
              </div>
            )}
            <div className="Multiple-images border rounded">
              {activeIndex === mergedTabs.length && (
                <ToolBomRelationTable />
              )}
              {(mergedTabs[activeIndex]?.data?.IsDimension === true ||
                String(
                  mergedTabs[activeIndex]?.data?.IsDimension
                ).toLowerCase() === 'true' ||
                mergedTabs[activeIndex]?.data?.isDimension === true ||
                String(
                  mergedTabs[activeIndex]?.data?.isDimension
                ).toLowerCase() === 'true') &&
                dims &&
                dims.length > 0 && (
                  <div className="tab_block border rounded">
                    <div className="tab_operator_inner">
                      <div className="tableDesign operator-dimension-table">
                        <div className="table-responsive">
                          <div className="react-bootstrap-table">
                            <table className="table table-hover table-bordered">
                              <thead>
                                <tr>
                                  {dims.map((dim: any) => (
                                    <th key={dim.DimensionColumn}>
                                      {dim.DimensionColumn}
                                    </th>
                                  ))}
                                </tr>
                              </thead>
                              <tbody>
                                {dimensionTable.map((row, rowIndex) => (
                                  <tr key={rowIndex}>
                                    {dims.map((dim: any) => {
                                      const rawValue = row[
                                        dim.DimensionColumn
                                      ] || {
                                        value: '',
                                        isDefaultChecked: false,
                                      };
                                      const { value, isDefaultChecked } =
                                        rawValue;
                                      const key = `${activeIndex}-${rowIndex}-${dim.Id}`;
                                      const legacyKey = `${rowIndex}-${dim.Id}`;
                                      const stringValue =
                                        value === null || value === undefined
                                          ? ''
                                          : String(value).trim();
                                      const hasValue =
                                        stringValue !== '' &&
                                        stringValue !== '-' &&
                                        stringValue !== '--';
                                      return (
                                        <td key={key}>
                                          {dim.DimensionType === 1 &&
                                          hasValue ? (
                                            <span className="d-flex align-items-center">
                                              <Form.Check
                                                type="checkbox"
                                                id={`dim-${key}`}
                                                checked={
                                                  dimensionChecks[key] ??
                                                  dimensionChecks[legacyKey] ??
                                                  isDefaultChecked
                                                }
                                                onChange={(e) =>
                                                  handleDimensionCheck(
                                                    activeIndex,
                                                    rowIndex,
                                                    dim.Id,
                                                    e.target.checked
                                                  )
                                                }
                                                disabled={!isTimerRunning}
                                              />
                                              <span className="ps-2">
                                                {value
                                                  .split('\n')
                                                  .map((line, index) => {
                                                    const displayLine =
                                                      line.trim() === ''
                                                        ? '\u00A0'
                                                        : line;
                                                    return (
                                                      <span
                                                        key={index}
                                                        className="d-block"
                                                      >
                                                        {displayLine}
                                                      </span>
                                                    );
                                                  })}
                                              </span>
                                            </span>
                                          ) : (
                                            <>
                                              {value
                                                .split('\n')
                                                .map((line, index) => {
                                                  const displayLine =
                                                    line.trim() === ''
                                                      ? '\u00A0'
                                                      : line;
                                                  return (
                                                    <span
                                                      key={index}
                                                      className="p-1 d-block"
                                                    >
                                                      {displayLine}
                                                    </span>
                                                  );
                                                })}
                                            </>
                                          )}
                                        </td>
                                      );
                                    })}
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              {mergedTabs[activeIndex]?.type === 'image' &&
                (allFilesAreNull ? (
                  <div className="message no-teplar-data">
                    <div className="d-flex message-area">
                      <div className="no-data-img text-center">
                        <img
                          src={img404}
                          alt={formatMessage('picture_label')}
                        />
                        <p>
                          {' '}
                          <FormattedMessage id="no_holiday_message" />{' '}
                        </p>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div>
                    {validFiles?.map((file: any, idx: number) => (
                      <div
                        key={idx}
                        className="images-operator-tabs text-center"
                      >
                        {file.FileType === 'application/pdf' ? (
                          <PdfViewer src={file.S3FilePath} />
                        ) : (
                          <CanvasDrawOnImage
                            imageUrl={file.S3FilePath}
                            initialData={
                              canvasDrawings[`${activeIndex}-${idx}`]
                            }
                            onSave={(dataUrl) =>
                              handleCanvasChange(
                                `${activeIndex}-${idx}`,
                                dataUrl
                              )
                            }
                          />
                          // <figure className="m-0">
                          //   <img
                          //     src={file.S3FilePath}
                          //     alt={file.Heading}
                          //   />
                          // </figure>
                        )}
                      </div>
                    ))}
                  </div>
                ))}
            </div>
          </div>
        </div>

        {/* Drag handle: mouse drags immediately, touch needs a long press */}
        <div
          className={`operator-split-handle${isPressing ? ' pressing' : ''}${isResizing ? ' active' : ''}`}
          style={{ flex: `0 0 ${SPLIT_HANDLE_PX}px`, width: SPLIT_HANDLE_PX }}
          role="separator"
          aria-orientation="vertical"
          aria-valuenow={Math.round(leftWidth)}
          aria-valuemin={MIN_COL_PERCENT}
          aria-valuemax={MAX_COL_PERCENT}
          onPointerDown={handleSplitPointerDown}
          onPointerMove={handleSplitPointerMove}
          onPointerUp={handleSplitPointerEnd}
          onPointerCancel={handleSplitPointerEnd}
          onContextMenu={(e) => e.preventDefault()}
        >
          <span className="operator-split-grip" />
        </div>

        <div
          className={`${Number(processType) === 3 ? 'picking-horizontal-right' : Number(processType) === 2 ? '' : 'print-horizontal'}`}
          style={{
            flex: `0 0 calc((100% - ${SPLIT_HANDLE_PX}px) * ${(100 - leftWidth) / 100})`,
            maxWidth: `calc((100% - ${SPLIT_HANDLE_PX}px) * ${(100 - leftWidth) / 100})`,
          }}
        >
          <div className="horizontal-right-section">
            {activeIndex === mergedTabs.length ? (
              // Tool BOM tab selected: show only the Target Tool List as a plain table -
              // no accordion, no Check Items/Memo, matching the other process-type tabs' content.
              <TargetToolList onInspectionOpen={onToolInspectionOpen} />
            ) : (
            <Accordion
              activeKey={activeAccordionKey}
              onSelect={(key) => setActiveAccordionKey((key as string) ?? '0')}
              className="horizontal-right-section-accordion"
            >
              {/* Print Table (if processType === 2) */}
              {Number(processType) === 2 && (
                <Accordion.Item
                  eventKey="0"
                  className="horizontal-right-section-top"
                >
                  <Accordion.Header>
                    {formatMessage('teplar_print')}
                  </Accordion.Header>
                  <Accordion.Body className="p-1">
                    <TeplarPrintTable
                      operatorTeplarDetails={operatorTeplarDetails}
                      isTimerRunning={isTimerRunning}
                      isProcessStarted={isProcessStarted}
                      printCount={printCount}
                      handleDecrementPrintCount={handleDecrementPrintCount}
                      handleIncrementPrintCount={handleIncrementPrintCount}
                      handleChangePrintCount={handleChangePrintCount}
                      stringToHex={stringToHex}
                      onSaveTeplarOrder={onSaveTeplarOrder}
                      onOrderChange={onTeplarOrderChange}
                      teplarList={teplarList}
                      setTeplarList={setTeplarList}
                      saveClicked={saveClicked}
                      setSaveClicked={setSaveClicked}
                    />
                  </Accordion.Body>
                </Accordion.Item>
              )}

              {/* Print BOM Table (if processType === 3) */}
              {(Number(processType) === 3 || Number(processType) === 4) && (
                <Accordion.Item
                  eventKey="0"
                  className="horizontal-right-section-top"
                >
                  <Accordion.Header>{formatMessage('bom')}</Accordion.Header>
                  <Accordion.Body className="p-1">
                    <BOMTable
                      bomDataList={bomDataList}
                      isTimerRunning={isTimerRunning}

                      setBomDataListState={setBomDataListState}
                      bomDataListState={bomDataListState}
                    />
                  </Accordion.Body>
                </Accordion.Item>
              )}

              {Number(processType) === 5 && (
                <Accordion.Item
                  eventKey="1"
                  className="horizontal-right-section-top"
                >
                  <Accordion.Header>
                    {formatMessage('led_cut')}
                  </Accordion.Header>
                  <Accordion.Body className="p-1">
                    <LEDCut
                      isTimerRunning={isTimerRunning}
                      ledCutList={ledCutListState}
                      setLedCutListState={setLedCutListState}
                      // openLEDHandler={openLEDHandler}
                    />
                  </Accordion.Body>
                </Accordion.Item>
              )}

              {Number(processType) === 6 && (
                <Accordion.Item
                  eventKey="2"
                  className="horizontal-right-section-top"
                >
                  <Accordion.Header>
                    {formatMessage('led_length')}
                  </Accordion.Header>
                  <Accordion.Body className="p-1">
                    <LEDLength
                      isTimerRunning={isTimerRunning}
                      openLEDHandler={openLEDHandler}
                      ledLengthList={ledLengthListState}
                      setLedLengthListState={setLedLengthListState}
                    />
                  </Accordion.Body>
                </Accordion.Item>
              )}

              <Accordion.Item
                eventKey='3'
                className="box2 horizontal-right-section-middle"
              >
                <Accordion.Header>
                  {formatMessage('check_items')}
                </Accordion.Header>
                <Accordion.Body className="p-1">
                  <CheckItemsData
                    checkItems={checkItems}
                    isTimerRunning={isTimerRunning}
                    activeIndex={activeIndex}
                    setSubmittedCheckItems={setSubmittedCheckItems}
                    submittedCheckItems={submittedCheckItems}
                  />
                </Accordion.Body>
              </Accordion.Item>
              {/* Box 2: Check Items and Memo */}
               <Accordion.Item
                eventKey='4'
                className="memo-message horizontal-right-section-bottom"
              >
                <Accordion.Header>
                  {formatMessage('memo_message')}
                </Accordion.Header>
                <Accordion.Body className="p-1">
                  <textarea
                    className="form-control"
                    maxLength={256}
                    placeholder={formatMessage('enter_message')}
                    value={
                      memo[
                        mergedTabs[activeIndex]?.data?.TabIndex ?? activeIndex
                      ] || ''
                    }
                    onChange={(e) => {
                      const memoKey =
                        mergedTabs[activeIndex]?.data?.TabIndex ?? activeIndex;
                      handleMemoChange(memoKey, e.target.value);
                    }}
                    disabled={!isTimerRunning}
                  />
                </Accordion.Body>
              </Accordion.Item>

            </Accordion>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default OperatorHorizontalView;
